import React, { useState, useEffect } from 'react';
import './Pricing.css';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';

const SMS_NUMBER = '5082031342';
const DISPLAY_NUMBER = '508-203-1342';

// ── Phone Modal (desktop fallback) ──────────────────────────────
const PhoneModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#fff', borderRadius: 16, padding: '32px 40px',
        textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
        maxWidth: 340, width: '90%',
      }}
    >
      <p style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5C96AC', marginBottom: 8 }}>
        Text Your Tattoo Photo
      </p>
      <p style={{ fontSize: 'clamp(1.6rem, 6vw, 2.2rem)', fontWeight: 800, color: '#1e3a5f', marginBottom: 8 }}>
        {DISPLAY_NUMBER}
      </p>
      <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 20 }}>
        Text us a photo of your tattoo to confirm your price per session.
      </p>
      <button
        onClick={onClose}
        style={{
          background: '#cb4f2e', color: '#fff', border: 'none', borderRadius: 50,
          padding: '10px 28px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
        }}
      >
        Close
      </button>
    </div>
  </div>
);

const PRICING_CARDS = [
  { size: 'Extra Small', price: '$80',  unit: 'per session', example: 'Example: Ring tattoo',                                         highlight: false },
  { size: 'Small',       price: '$150', unit: 'per session', example: 'Example: Smaller than a palm',                                  highlight: false },
  { size: 'Medium',      price: '$200', unit: 'per session', example: 'Example: Bigger than a palm, smaller than a half sleeve',       highlight: true  },
];

const Pricing: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowStickyBar(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {showPhoneModal && <PhoneModal onClose={() => setShowPhoneModal(false)} />}
      <Helmet>
        <title>Tattoo Removal Pricing | Atomic Tattoo Removal</title>
        <meta
          name="description"
          content="Transparent laser tattoo removal pricing starting at $80 per session. Free consultations — get exact pricing for your tattoo before treatment begins. Wrentham, MA."
        />
        <meta
          name="keywords"
          content="tattoo removal pricing, laser tattoo removal cost, tattoo removal packages, tattoo removal consultation, Atomic Tattoo Removal pricing"
        />
        <meta property="og:title" content="Tattoo Removal Pricing | Atomic Tattoo Removal" />
        <meta
          property="og:description"
          content="Transparent laser tattoo removal pricing starting at $80 per session. Free consultations — get exact pricing for your tattoo before treatment begins."
        />
        <meta property="og:image" content="https://www.atomictattooremoval.com/assets/pricing-preview.jpg" />
        <meta property="og:url" content="https://www.atomictattooremoval.com/pricing" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Laser Tattoo Removal",
            "description": "Safe and effective laser tattoo removal services tailored to your needs.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Atomic Tattoo Removal",
              "url": "https://www.atomictattooremoval.com"
            },
            "areaServed": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Wrentham",
                "addressRegion": "MA",
                "postalCode": "02093"
              }
            },
            "offers": [
              { "@type": "Offer", "name": "Extra Small", "priceCurrency": "USD", "price": "80" },
              { "@type": "Offer", "name": "Small",       "priceCurrency": "USD", "price": "150" },
              { "@type": "Offer", "name": "Medium",      "priceCurrency": "USD", "price": "200" }
            ]
          }
          `}
        </script>
      </Helmet>

      <div className="pricing-page">

        {/* ── HERO ─────────────────────────────────────────── */}
        <section className="pricing-hero">
          <h1>Tattoo Removal Pricing</h1>
          <p className="pricing-hero-tagline">Honest pricing. No surprises.</p>
          <p className="pricing-hero-body">
            Every tattoo is different, so number of sessions needed depends on size, ink density, color, location,
            previous treatments, how saturated the tattoo is and your immune system (<a href="#factors" style={{ color: '#5C96AC', fontWeight: 600, textDecoration: 'underline' }}>see more factors below</a>).  The industry average is between 6 and 12 sessions for full removal.  6-8 weeks between sessions is needed to allow your skin to fully heal before the next laser treatment.
          </p>
        </section>

        {/* ── LOCAL SERVICE AREA ───────────────────────────── */}
        <div className="pricing-local-trust">
          📍 Serving Wrentham, Franklin, Foxboro, Norfolk, Plainville, and surrounding Massachusetts areas.  We're near Rhode Island too!
        </div>

        {/* ── PRICING CARDS ────────────────────────────────── */}
        <section className="pricing-cards-section">
          <h2>Session Pricing Ranges</h2>
          <p className="pricing-section-note">
            These are starting ranges per session. Your session pricing will be confirmed during your free consultation.
          </p>

          <div className="pricing-cards-grid">
            {PRICING_CARDS.map((card) => (
              <div key={card.size} className={`pricing-card${card.highlight ? ' pricing-card--featured' : ''}`}>
                <div className="pricing-card-size">{card.size}</div>
                <div className="pricing-card-price">{card.price}</div>
                <div className="pricing-card-unit">{card.unit}</div>
                <div className="pricing-card-example">{card.example}</div>
              </div>
            ))}
          </div>

          <div className="pricing-larger-work">
            <h3>Larger Work</h3>
            <div className="pricing-larger-items">
              <div className="pricing-larger-item">
                <span className="pricing-larger-label">½ Sleeve</span>
                <span className="pricing-larger-value">~$400 per session</span>
                <span className="pricing-larger-note">Equivalent to 2 mediums</span>
              </div>
              <div className="pricing-larger-item">
                <span className="pricing-larger-label">Full Sleeve</span>
                <span className="pricing-larger-value">~$800 per session</span>
                <span className="pricing-larger-note">Equivalent to 4 mediums</span>
              </div>
            </div>
            <p className="pricing-larger-disclaimer">
              Larger pieces require multiple sessions to treat, the pricing takes that into account.  For example, a full sleeve would require 4 sessions, $200 per session = $800 to treat the entire sleeve.
            </p>
          </div>

          <p className="pricing-payment-note">
            <Link to="/payment-plans" style={{ color: '#5C96AC', fontWeight: 600, textDecoration: 'underline' }}>
              Flexible Payment Plans
            </Link>{' '}
            are available on packages. We also accept all major credit cards.
          </p>
        </section>

        {/* ── PACKAGE DISCOUNTS ────────────────────────────── */}
        <section className="pricing-packages-section">
          <h2>Save With Session Packages</h2>
          <p>
            Discounts up to 15% are available when purchasing one of our treatment packages. Package
            pricing is reviewed at your consultation so we can match a plan to your tattoo and your goals.
          </p>
        </section>

        {/* ── SESSION EXPECTATIONS ─────────────────────────── */}
        <section className="pricing-expectations-section">
          <h2>Full Removal Takes Time</h2>
          <p>
            Most tattoos require 6–12 sessions for full removal, and highly saturated or deep tattoos
            may require more.
          </p>
          <p>We believe in realistic expectations, not false promises.</p>
        </section>

        {/* ── CUSTOMER REFERRAL PROGRAM ────────────────────────────── */}
        <section className="pricing-packages-section">
          <h2>Save EVEN MORE by referring your friends</h2>
          <p>
            Advertising costs money.  We'd rather reward our customers for spreading the word than spend that money on ads.
          </p>
          <p>
            Once you're a client, when you refer a friend and they purchase a treatment package, you get a free session.  There is no limit on how many friends you can refer.
          </p>
        </section>        

        {/* ── PRICING CTA ──────────────────────────────────── */}
        <section className="pricing-cta-section">
          <h2>Want to Confirm Your Pricing?</h2>
          <p className="pricing-cta-guidance">
            Send us a quick photo of your tattoo for personalized pricing and realistic fading expectations.
          </p>
          <div className="pricing-cta-buttons">
            {isMobile ? (
              <a
                href={`sms:${SMS_NUMBER}`}
                className="pricing-cta-sms pricing-cta-sms--primary"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ event: 'sms_click', button_location: 'pricing_cta' });
                }}
              >
                📱 Text Your Tattoo Photo
              </a>
            ) : (
              <button
                className="pricing-cta-sms pricing-cta-sms--primary"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ event: 'sms_click', button_location: 'pricing_cta' });
                  setShowPhoneModal(true);
                }}
              >
                📱 Text Your Tattoo Photo
              </button>
            )}
            <Link to="/book-consultation" style={{ textDecoration: 'none' }}>
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ event: 'book_now_click', button_location: 'pricing_cta' });
                }}
                sx={{
                  color: '#f0e9d4',
                  borderColor: 'rgba(240,233,212,0.5)',
                  '&:hover': { color: '#ffffff', borderColor: '#ffffff', backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Book Free Consultation
              </Button>
            </Link>
          </div>
          <p className="pricing-cta-trust">
            All texts are answered directly by owner Craig Howard — not a robot 🤖 or call center.
          </p>
        </section>

        {/* ── WHY EVERY TATTOO REMOVES DIFFERENTLY ─────────── */}
        <section id="factors" className="pricing-factors-section">
          <h2>Why Every Tattoo Removes Differently</h2>
          <p>
            The number of sessions needed can vary widely based on a combination of factors unique to
            your tattoo and your body. Understanding these helps set realistic expectations:
          </p>

          <div style={{ backgroundColor: '#5C96AC', padding: '20px', margin: '0' }}>
            <div className="factors-grid">
              <div className="factor-item">
                <h3>⏳ 1. Tattoo Age</h3>
                <p>
                  Older tattoos are usually easier to remove. Over time, the body naturally fades ink
                  as it breaks down and flushes out pigment particles.
                </p>
                <p>
                  Newer tattoos often require more sessions because the ink is still fresh, dense, and
                  hasn't degraded.
                </p>
              </div>
              <div className="factor-item">
                <h3>🎨 2. Ink Color</h3>
                <p>Black ink absorbs all laser wavelengths and is the easiest to remove.</p>
                <p>Red, orange, brown – typically respond well to 532 nm lasers.</p>
                <p>
                  Green, blue, turquoise, purple – these vibrant colors are more stubborn and need
                  specialized wavelengths like 694 nm (used in the Trinity system).
                </p>
                <p>Multicolored tattoos = more complexity = more sessions.</p>
              </div>
              <div className="factor-item">
                <h3>🖋️ 3. Ink Density &amp; Layering</h3>
                <p>Dense, solid tattoos (e.g., blackouts or heavy shading) take longer to break apart.</p>
                <p>
                  Layered tattoos (cover-ups) are especially tough, as there's usually more ink at
                  different depths.
                </p>
              </div>
              <div className="factor-item">
                <h3>📏 4. Size of the Tattoo</h3>
                <p>
                  Larger tattoos take more time per session and typically more sessions overall, simply
                  because there's more ink to treat.
                </p>
              </div>
              <div className="factor-item">
                <h3>📍 5. Tattoo Location</h3>
                <p>
                  Areas with better blood circulation (like the chest or upper arms) tend to heal and
                  flush out ink faster.
                </p>
                <p>Areas with poor circulation (like ankles, fingers, or feet) often take longer.</p>
              </div>
              <div className="factor-item">
                <h3>🧬 6. Skin Type and Tone</h3>
                <p>
                  Darker skin tones may require lower laser settings to avoid pigment changes
                  (hyperpigmentation or hypopigmentation), which can slow down the process.
                </p>
                <p>
                  Fitzpatrick Skin Types I–III (lighter skin tones) tend to tolerate higher fluences
                  and may respond faster.
                </p>
              </div>
              <div className="factor-item">
                <h3>💪 7. Immune System Health</h3>
                <p>
                  Tattoo removal relies heavily on your immune system to flush out shattered ink
                  particles.
                </p>
                <p>
                  People who are healthy, active, and hydrated generally see faster fading between
                  sessions.
                </p>
              </div>
              <div className="factor-item">
                <h3>🚬 8. Lifestyle Factors</h3>
                <p>Smokers tend to see slower progress.</p>
                <p>
                  Poor nutrition, low hydration, or compromised liver/kidney function can reduce ink
                  clearance.
                </p>
              </div>
              <div className="factor-item">
                <h3>🔁 9. Time Between Sessions</h3>
                <p>
                  Most providers space sessions 6–8 weeks apart to allow full healing and for the body
                  to flush out ink.
                </p>
                <p>Rushing sessions doesn't help and can increase the risk of side effects.</p>
              </div>
              <div className="factor-item">
                <h3>🔧 10. Type of Laser Technology Used</h3>
                <p>
                  Advanced systems like the Astanza Trinity (with multiple wavelengths) can treat all
                  ink colors effectively and often reduce the number of required sessions.
                </p>
                <p>
                  Picosecond lasers like PicoStar® may remove ink in fewer sessions than traditional
                  nanosecond lasers due to their shorter pulse duration and higher peak power.
                </p>
              </div>
              <div className="factor-item">
                <h3>🧑‍🎨 11. Professional vs. Amateur Tattoos</h3>
                <p>
                  Professional tattoos often use more ink, at a consistent depth — harder to remove.
                </p>
                <p>
                  Amateur tattoos tend to be lighter and uneven, and may clear faster (though sometimes
                  they're unpredictable).
                </p>
              </div>
              <div className="factor-item">
                <h3>🧴 12. Aftercare</h3>
                <p>
                  Following proper aftercare (keeping the area clean, avoiding sun exposure,
                  moisturizing, and not picking at scabs) prevents complications and supports faster
                  healing and fading.
                </p>
              </div>
            </div>
          </div>

          <div className="pricing-factors-footer">
            <h3>On Average</h3>
            <p>
              Most clients need 6–12 sessions for significant fading or full removal. Some tattoos fade
              quickly, others take longer — it depends on the factors above. We'll give you an honest
              estimate at your free consultation.
            </p>
            <Link to="/book-consultation" style={{ textDecoration: 'none' }}>
              <Button
                color="secondary"
                variant="contained"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ event: 'book_now_click', button_location: 'pricing_page_bottom' });
                }}
                sx={{ color: '#f0e9d4', '&:hover': { color: '#ffffff', backgroundColor: '#cb4f2e' } }}
              >
                👉 Book Your Free Consultation
              </Button>
            </Link>
          </div>
        </section>

      </div>

      {/* ── MOBILE STICKY CTA ────────────────────────────── */}
      {isMobile && showStickyBar && (
        <div className="pricing-sticky-bar">
          <a
            href={`sms:${SMS_NUMBER}`}
            className="pricing-sticky-sms"
            onClick={() => {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: 'sms_click', button_location: 'pricing_sticky' });
            }}
          >
            📱 Text Us
          </a>
          <Link
            to="/book-consultation"
            className="pricing-sticky-book"
            onClick={() => {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: 'book_now_click', button_location: 'pricing_sticky' });
            }}
          >
            Book Consultation
          </Link>
        </div>
      )}
    </>
  );
};

export default Pricing;
